import React from "react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Share2, Link2, Check } from "lucide-react";
import { toast } from "sonner";

// Minimal brand glyphs (lucide dropped brand icons, so inline SVG keeps them recognisable)
const WhatsApp = (p) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.82 9.82 0 001.59 5.312l-.999 3.648 3.909-.659zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.148-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
);
const Linkedin = (p) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z"/></svg>
);
const XLogo = (p) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
);
const Facebook = (p) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
);

const ShareMenu = ({ job, className = "" }) => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const url = `${origin}/opportunities?job=${job.id}`;
  const text = `${job.role_title} at ${job.organisation_name}${job.location ? " — " + job.location : ""} on CareHR`;
  const eu = encodeURIComponent(url);
  const et = encodeURIComponent(text);

  const open = (link) => window.open(link, "_blank", "noopener,noreferrer");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied", { description: "Share it anywhere you like." });
    } catch {
      toast.error("Could not copy link");
    }
  };

  const Item = ({ icon: Icon, label, onClick }) => (
    <DropdownMenuItem onClick={onClick} className="cursor-pointer gap-2.5 py-2">
      <Icon className="h-4 w-4 text-slate-600" width={16} height={16} />
      <span className="text-[14px] font-medium text-slate-700">{label}</span>
    </DropdownMenuItem>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Share opportunity"
          className={`flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:border-[#1E4FA3]/50 hover:text-[#1E4FA3] ${className}`}
        >
          <Share2 className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <Item icon={WhatsApp} label="WhatsApp" onClick={() => open(`https://wa.me/?text=${encodeURIComponent(text + " " + url)}`)} />
        <Item icon={Linkedin} label="LinkedIn" onClick={() => open(`https://www.linkedin.com/sharing/share-offsite/?url=${eu}`)} />
        <Item icon={XLogo} label="X (Twitter)" onClick={() => open(`https://twitter.com/intent/tweet?text=${et}&url=${eu}`)} />
        <Item icon={Facebook} label="Facebook" onClick={() => open(`https://www.facebook.com/sharer/sharer.php?u=${eu}`)} />
        <DropdownMenuSeparator />
        <Item icon={Link2} label="Copy link" onClick={copy} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ShareMenu;
